import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { Link, NavLink } from "react-router-dom";
import { Navbar, Nav, Container } from "react-bootstrap";

import { NotificationBellIcon } from "../assets";
import CLOSE_ICON from "../assets/icons/close.png";
import logoIcon from "../assets/images/logo-icon.png";

import { ReactComponent as FavouriteIcon } from "../assets/icons/navbar/favourite.svg";
import { ReactComponent as KnowledgeBaseIcon } from "../assets/icons/navbar/encyclopedia.svg";
import {
  USER_ROLE,
  APPROVAL_STATUS,
  SUBSCRIPTION_STATUS,
} from "../system/global-enums";
import { setItemToStorage } from "../system";
import { SCREEN_PATH, EMPTY_STRING } from "../constants/global.constants";
import {
  hasAgentAccess,
  hasAdminAccess,
  relativeDateTime,
} from "../shared/utility";
import { homeActions, authActions, adminActions } from "../redux/actions";
import UserDropdown from "./UserDropdown";
import CurrencyDropdown from "./CurrencyDropdown";

const Topbar = (props) => {
  const {
    user,
    userRole,
    allCurrencies,
    defaultCurrency,
    updateUserRole,
    handleShowSidebar = () => {},
    openPasswordModal = () => {},
    updateDefaultCurrency = () => {},
    getAdminNotifications,
    adminNotifications,
    updateAdminNotificationStatus,
  } = props;

  const hasAgentRole = hasAgentAccess(userRole);
  const hasAdminRole = hasAdminAccess(userRole);
  const isMultipleRoles = user.roleIds.length > 1;
  const isAgentApproved = user.approvalStatus === APPROVAL_STATUS.APPROVED;

  const notificationPanelRef = useRef(null);
  const [isNotifyPanelOpen, setIsNotifyPanelOpen] = useState(false);

  const isNotSubscribed =
    user?.subscriptionStatus === SUBSCRIPTION_STATUS.NOT_SUBSCRIBED;

  const RenderContent = ({ children }) => {
    if (hasAdminRole) {
      return <>{children}</>;
    } else {
      return (
        <Navbar.Collapse id="basic-navbar-nav">{children}</Navbar.Collapse>
      );
    }
  };

  const getDefaultPathToNavigate = () => {
    if (hasAdminRole) {
      return SCREEN_PATH.ADMIN_DASHBOARD;
    } else if (hasAgentRole) {
      if (user.approvalStatus !== APPROVAL_STATUS.APPROVED) {
        return SCREEN_PATH.AGENT_PROFILE;
      }
      return SCREEN_PATH.MY_LISTING;
    } else {
      return SCREEN_PATH.HOME;
    }
  };

  const handleNotifications = (notificationId) => {
    updateAdminNotificationStatus(notificationId);
  };

  // Memoize the function to prevent re-creating it on each render
  const openNotificationPanel = useCallback(() => {
    setIsNotifyPanelOpen((prev) => !prev);
  }, []);

  // Close the notification panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is outside of the notification panel and notification bell
      if (
        notificationPanelRef.current &&
        !notificationPanelRef.current.contains(event.target)
      ) {
        openNotificationPanel();
      }
    };

    // Add event listener to listen for clicks
    document.addEventListener("mousedown", handleClickOutside);

    // Cleanup event listener when the component is unmounted
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openNotificationPanel]);

  useEffect(() => {
    if (hasAdminRole) {
      getAdminNotifications();
    }
  }, []);

  useEffect(() => {
    setItemToStorage("defaultCurrency", defaultCurrency);
  }, [defaultCurrency]);

  return (
    <Navbar
      expand={hasAdminRole ? "md" : "lg"}
      bg="white"
      className={`navbar align-items-center navbar__container ${
        hasAdminRole ? "admin-nav-bottom-border" : EMPTY_STRING
      }`}
      fixed="top"
    >
      <Container className={`${hasAdminRole ? "admin-nav" : EMPTY_STRING}`}>
        <Navbar.Brand
          as={Link}
          to={getDefaultPathToNavigate()}
          className={`d-flex align-items-center ${
            hasAdminRole ? "d-none d-md-flex" : EMPTY_STRING
          }`}
        >
          <img src={logoIcon} height={46} width={46} />
          <p className="brand-logo">Bayview Shores</p>
        </Navbar.Brand>
        <Navbar.Toggle
          aria-controls="basic-navbar-nav"
          onClick={() => {
            hasAdminRole && handleShowSidebar();
          }}
        />

        <RenderContent>
          <Nav
            className={`align-items-start align-items-lg-center pt-0 mt-lg-0 mt-0 ${
              hasAdminRole
                ? "flex-row gap-3 align-items-center"
                : "column-gap-3"
            }`}
          >
            {hasAdminRole ? (
              <div>
                <Nav.Link
                  as={Link}
                  to={SCREEN_PATH.ADMIN_DASHBOARD}
                  className="nav-item admin-name line-clamp lh-1-7"
                >
                  {user?.name ?? "Admin"}
                </Nav.Link>
              </div>
            ) : hasAgentRole ? (
              <>
                {!user.hasMultipleRoles && (
                  <Nav.Link
                    as={Link}
                    to={"/Buy-Property"}
                    className="nav-item-btn my-2 my-lg-0"
                  >
                    Buy
                  </Nav.Link>
                )}
                {user.hasMultipleRoles && isMultipleRoles && (
                  <Nav.Link
                    as={Link}
                    to={SCREEN_PATH.HOME}
                    className="nav-item-btn my-2 my-lg-0"
                    onClick={() => updateUserRole(USER_ROLE.USER)}
                  >
                    Buy
                  </Nav.Link>
                )}
                <div className="position-relative">
                  <Nav.Link
                    as={Link}
                    to={SCREEN_PATH.MY_LISTING}
                    className={`nav-item px-lg-1 p-0 py-2 ${
                      user.approvalStatus !== APPROVAL_STATUS.APPROVED
                        ? "pe-none"
                        : EMPTY_STRING
                    }`}
                  >
                    My Listings
                  </Nav.Link>
                  <div className="position-absolute w-100 d-flex justify-content-center blue-dot-container">
                    <div className="listing-blue-dot" />
                  </div>
                </div>
              </>
            ) : (
              <>
                {!user.hasMultipleRoles && (
                  <Nav.Link
                    as={Link}
                    to={"/Sell-With-Us"}
                    className="nav-item-btn my-2 my-lg-0"
                  >
                    Sell
                  </Nav.Link>
                )}
                {user.hasMultipleRoles && isMultipleRoles && (
                  <Nav.Link
                    as={Link}
                    to={
                      isAgentApproved
                        ? SCREEN_PATH.MY_LISTING
                        : SCREEN_PATH.AGENT_PROFILE
                    }
                    className="nav-item-btn my-2 my-lg-0"
                    onClick={() => updateUserRole(USER_ROLE.AGENT)}
                  >
                    Sell
                  </Nav.Link>
                )}
                {/* knowledge base item */}
                <Nav.Link
                  as={NavLink}
                  to={SCREEN_PATH.COUNTRIES_ENCYCLOPEDIA}
                  className="nav-item px-lg-1 pt-0 mb-2 mb-lg-0 pb-1"
                >
                  <KnowledgeBaseIcon className="icon" />
                </Nav.Link>
                {/* favourites item */}
                <Nav.Link
                  as={NavLink}
                  to={SCREEN_PATH.FAVOURITES}
                  className="nav-item px-lg-1 pt-0 mb-2 mb-lg-0 pb-1"
                >
                  <FavouriteIcon className="icon" />
                </Nav.Link>
                {/* currency item */}
                <div className="nav-item px-lg-1 p-0  mb-2 mb-lg-0">
                  <CurrencyDropdown
                    isFromTopbar
                    allCurrencies={allCurrencies}
                    selectedCurrency={defaultCurrency}
                    onCurrencyChange={updateDefaultCurrency}
                  />
                </div>
              </>
            )}

            <UserDropdown
              role={
                hasAdminRole
                  ? USER_ROLE.ADMIN
                  : hasAgentRole
                  ? USER_ROLE.AGENT
                  : USER_ROLE.USER
              }
              openPasswordModal={openPasswordModal}
              isNotSubscribed={isNotSubscribed}
              isAgentApproved={isAgentApproved}
              isPaymentDisabled={user?.isPaymentDisabled}
            />
            {hasAdminRole && (
              <>
                <div
                  className="position-relative ms-2 main-notification"
                  onClick={() => openNotificationPanel()}
                >
                  <img src={NotificationBellIcon} alt={EMPTY_STRING} />
                  <div className="notification-badge">
                    {adminNotifications?.total || 0}
                  </div>
                </div>
                {isNotifyPanelOpen && (
                  <div
                    className="notification animate__animated animate__fadeIn"
                    ref={notificationPanelRef}
                  >
                    {adminNotifications?.items?.length > 0 ? (
                      adminNotifications?.items?.map((item, index) => {
                        return (
                          <div key={index} className="row notification-box">
                            <div className="col-1 d-flex align-items-center">
                              <i className="notification-box-circle"></i>
                            </div>
                            <div className="col-10 p-0 m-0 d-flex align-items-center">
                              <p className="notification-text">
                                {item.message}
                              </p>
                            </div>
                            <div className="col-1 d-flex align-items-center">
                              <span>
                                <img
                                  className="close-icon"
                                  src={CLOSE_ICON}
                                  alt="close"
                                  onClick={() => handleNotifications(item.id)}
                                />
                              </span>
                            </div>
                            <div className="row">
                              <div className="col-1" />
                              <div className="col-11 p-0">
                                <p className="notification-date ps-1">
                                  {relativeDateTime(item.createdAt)}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <p className="notification-text d-flex justify-content-center w-100">
                        No Notifications
                      </p>
                    )}
                  </div>
                )}
              </>
            )}
          </Nav>
        </RenderContent>
      </Container>
    </Navbar>
  );
};

const mapStateToProps = (state) => ({
  user: state.authReducer.user,
  userRole: state.authReducer.userRole,
  allCurrencies: state.homeReducer.allCurrencies,
  defaultCurrency: state.homeReducer.defaultCurrency,
  adminNotifications: state.adminReducer.adminNotifications,
});

const mapDispatchToProps = (dispatch) => ({
  updateDefaultCurrency: (id) =>
    dispatch(homeActions.updateDefaultCurrency(id)),
  updateUserRole: (id) => dispatch(authActions.updateUserRole(id)),
  getAdminNotifications: () => dispatch(adminActions.getAdminNotifications()),
  updateAdminNotificationStatus: (id) =>
    dispatch(adminActions.updateAdminNotificationStatus(id)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Topbar);
